import { Alert, AlertDescription, Button, Input, Label } from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';


import { login } from '../../api/auth.js';
import { ApiError } from '../../lib/api-client.js';

import { AuthShell } from './auth-shell.js';

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);

  const mutation = useMutation({
    mutationFn: () => login({ email, password, totpCode: totpCode || undefined }),
    onSuccess: () => navigate('/dashboard', { replace: true }),
    onError: (err) => {
      if (err instanceof ApiError && err.message === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
      }
    },
  });

  return (
    <AuthShell
      title="Sign in"
      description="Use your work email and password to access this organization."
      footer={
        <span>
          Need an account?{' '}
          <Link to="/forgot-password" className="font-medium text-fg hover:underline">
            Reset your password
          </Link>
        </span>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        noValidate
      >
        <FieldGroup label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FieldGroup>

        <FieldGroup label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FieldGroup>

        {needsTotp ? (
          <FieldGroup label="6-digit code" htmlFor="totp">
            <Input
              id="totp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D+/g, ''))}
              autoFocus
            />
          </FieldGroup>
        ) : null}

        {mutation.error && !(mutation.error instanceof ApiError && mutation.error.message === 'TOTP_REQUIRED') ? (
          <Alert variant="danger">
            <AlertDescription>
              {mutation.error instanceof ApiError
                ? mutation.error.message
                : 'Sign-in failed. Please try again.'}
            </AlertDescription>
          </Alert>
        ) : null}

        <Button type="submit" className="w-full" disabled={mutation.isPending}>
          {mutation.isPending ? 'Signing in…' : needsTotp ? 'Verify code' : 'Sign in'}
        </Button>

        <p className="text-center text-xs text-fg-muted">
          Vendor?{' '}
          <Link to="/vendor" className="font-medium text-fg hover:underline">
            Sign in with email link
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

function FieldGroup({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}
